/**
 * QrScanner — camera scan pentru QR codes.
 *
 * Design:
 *  - Buton "Scanează codul QR" initial
 *  - La apasare: deschide camera, afiseaza video cu overlay (chenar animat)
 *  - La detectare QR: flash verde, chenar pulse, callback onScan(decodedText)
 *  - Mesaj de eroare daca camera refuza accesul
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import jsQR from 'jsqr';

interface QrScannerProps {
  /** Apelata cand se decodeaza un QR cu succes. */
  onScan: (decodedText: string) => void;
  /** Mesaj sub titlu (optional). */
  hint?: string;
}

type Status = 'idle' | 'requesting' | 'scanning' | 'denied' | 'unsupported' | 'error';

export function QrScanner({ onScan, hint }: QrScannerProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [detectedFlash, setDetectedFlash] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);

  const stop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStatus('idle');
  }, []);

  useEffect(() => {
    return () => stop();
  }, [stop]);

  const start = useCallback(async () => {
    setError(null);

    // Verificam suport API
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('unsupported');
      setError('Acest browser nu suportă camera. Folosește un browser modern.');
      return;
    }

    setStatus('requesting');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) {
        setStatus('error' as Status);
        return;
      }
      video.srcObject = stream;
      await video.play();
      setStatus('scanning');

      // Loop de detectare: 10 fps e suficient pentru QR static
      const tick = () => {
        if (!videoRef.current || !canvasRef.current || !streamRef.current) return;
        const v = videoRef.current;
        const c = canvasRef.current;
        if (v.readyState !== v.HAVE_ENOUGH_DATA) {
          rafRef.current = requestAnimationFrame(tick);
          return;
        }
        c.width = v.videoWidth;
        c.height = v.videoHeight;
        const ctx = c.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;
        ctx.drawImage(v, 0, 0, c.width, c.height);
        const imageData = ctx.getImageData(0, 0, c.width, c.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert',
        });
        if (code && code.data) {
          // Flash + stop camera + callback
          setDetectedFlash(true);
          setTimeout(() => setDetectedFlash(false), 800);
          stop();
          onScan(code.data);
          return;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      if (
        msg.toLowerCase().includes('denied') ||
        msg.toLowerCase().includes('permission') ||
        e?.name === 'NotAllowedError'
      ) {
        setStatus('denied');
        setError('Ai refuzat accesul la cameră. Reîncarcă și permite.');
      } else {
        setStatus('error');
        setError(msg || 'Camera nu a putut fi deschisă');
      }
    }
  }, [onScan, stop]);

  return (
    <div className="w-full max-w-md">
      <AnimatePresence mode="wait">
        {status === 'idle' && (
          <motion.button
            key="start"
            type="button"
            onClick={start}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="btn-primary w-full"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
            </svg>
            Scanează codul QR
          </motion.button>
        )}

        {(status === 'requesting' || status === 'scanning') && (
          <motion.div
            key="scanning"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.4 }}
            className="relative overflow-hidden rounded-lg border-2 border-accent"
            style={{ boxShadow: 'var(--shadow-lg)' }}
          >
            {/* Video feed */}
            <video
              ref={videoRef}
              className="block aspect-video w-full bg-bg-alt object-cover"
              playsInline
              muted
            />
            <canvas ref={canvasRef} className="hidden" />

            {/* Scanning overlay - animated corner brackets */}
            <div className="pointer-events-none absolute inset-0">
              {/* Corner brackets */}
              <CornerBracket className="left-md top-md" rotate={0} />
              <CornerBracket className="right-md top-md" rotate={90} />
              <CornerBracket className="right-md bottom-md" rotate={180} />
              <CornerBracket className="left-md bottom-md" rotate={270} />

              {/* Animated scan line */}
              <motion.div
                className="absolute left-md right-md h-px bg-accent"
                initial={{ top: '20%' }}
                animate={{ top: ['20%', '80%', '20%'] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                style={{ boxShadow: '0 0 12px rgba(196, 102, 79, 0.6)' }}
              />
            </div>

            {/* Detection flash */}
            <AnimatePresence>
              {detectedFlash && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 bg-success"
                  style={{ mixBlendMode: 'screen' }}
                />
              )}
            </AnimatePresence>

            {/* Status pill */}
            <div className="absolute left-1/2 top-md -translate-x-1/2 rounded-full bg-fg/85 px-md py-xs backdrop-blur">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-bg">
                {status === 'requesting' ? 'Se deschide camera…' : 'Caut cod QR'}
              </p>
            </div>

            {/* Cancel button */}
            <button
              type="button"
              onClick={stop}
              className="absolute bottom-md right-md rounded-full bg-bg/90 px-md py-xs text-xs text-fg-soft backdrop-blur hover:text-fg"
            >
              Anulează
            </button>
          </motion.div>
        )}

        {(status === 'denied' || status === 'unsupported' || status === 'error') && (
          <motion.div
            key="error"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-lg border border-error bg-bg-alt p-md text-sm text-error"
          >
            <p className="font-medium">Camera indisponibilă</p>
            <p className="mt-xs text-error/80">{error}</p>
            <button
              type="button"
              onClick={() => setStatus('idle')}
              className="mt-sm text-xs underline-offset-2 hover:underline"
            >
              Încearcă din nou
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {hint && status === 'idle' && (
        <p className="mt-sm text-center text-xs text-fg-soft">{hint}</p>
      )}
    </div>
  );
}

function CornerBracket({ className, rotate }: { className?: string; rotate: number }) {
  return (
    <svg
      className={`absolute h-8 w-8 text-accent ${className ?? ''}`}
      style={{ transform: `rotate(${rotate}deg)` }}
      viewBox="0 0 32 32"
      fill="none"
    >
      <path
        d="M2 12V4a2 2 0 0 1 2-2h8"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
